import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SessionService } from 'src/app/shared/helpers/sesion.service';
import { SectoresService } from '../../services/sectores.service';
import { IOpcionesRequest, IRelacionRequest, IRelacionResponse, IRelacionItem } from '../../interfaces/sector.interface';

@Component({
  selector: 'app-finanza-form',
  templateUrl: './finanza-form.component.html'
})
export class FinanzaFormComponent implements OnInit, OnChanges {
  @Input() sector: string = '';
  @Input() descripcion: string = '';
  @Input() isEditMode: boolean = false;
  @Input() itemToEdit: any = null;
  @Output() saved = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading: boolean = false;
  tipoEstadoOptions: any[] = [];
  variableFinancieraOptions: any[] = [];

  constructor(
    private fb: FormBuilder,
    private sessionService: SessionService,
    private sectoresService: SectoresService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadDefaultValues();
    this.loadTipoEstadoOptions();
    this.loadVariableFinancieraOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sector']) {
      this.loadDefaultValues();
    }
    if (changes['itemToEdit'] && changes['itemToEdit'].currentValue) {
      this.loadFinanza();
    }
  }

  loadFinanza(): void {
    if (!this.itemToEdit) return;
    
    console.log('Cargando datos para edición:', this.itemToEdit);
    console.log('Fecha desde original:', this.itemToEdit.desde);
    console.log('Fecha desde convertida:', this.convertDateFromDDMMYYYY(this.itemToEdit.desde));
    console.log('Fecha hasta original:', this.itemToEdit.hasta);
    console.log('Fecha hasta convertida:', this.convertDateFromDDMMYYYY(this.itemToEdit.hasta));
    
    this.form.patchValue({
      sector: this.itemToEdit.sector,
      desde: this.convertDateFromDDMMYYYY(this.itemToEdit.desde),
      hasta: this.convertDateFromDDMMYYYY(this.itemToEdit.hasta),
      tipoEstado: this.itemToEdit.tipo,
      variableFinanciera: this.itemToEdit.variableFinanciera,
      valorMinimo: this.itemToEdit.valorMinimo,
      valorMaximo: this.itemToEdit.valorMaximo,
      valorPromedio: this.itemToEdit.valorPromedio,
      responsable: this.itemToEdit.responsable
    });
    // Deshabilitar campos que no se pueden editar
    this.form.get('sector')?.disable();
    this.form.get('desde')?.disable();
    this.form.get('hasta')?.disable();
    this.form.get('tipoEstado')?.disable();
    this.form.get('variableFinanciera')?.disable();
    this.form.get('responsable')?.disable();
  }

  initForm(): void {
    this.form = this.fb.group({
      sector: ['', Validators.required],
      desde: ['', Validators.required],
      hasta: ['', Validators.required],
      tipoEstado: ['', Validators.required],
      variableFinanciera: ['', Validators.required],
      valorMinimo: ['', Validators.required],
      valorMaximo: ['', Validators.required],
      valorPromedio: ['', Validators.required],
      responsable: ['', Validators.required]
    });
  }

  loadDefaultValues(): void {
    const userSession = this.sessionService.getUserSession();
    const sUsuario = userSession && userSession.trim() !== '' ? userSession : 'admin';
    
    if (this.form && !this.isEditMode) {
      // Solo para modo nuevo: cargar valores por defecto
      this.form.patchValue({
        sector: this.sector,
        responsable: sUsuario
      });
      // Deshabilitar campos de solo lectura
      this.form.get('sector')?.disable();
      this.form.get('responsable')?.disable();
    }
  }

  convertDateFromDDMMYYYY(dateString: string): string {
    if (!dateString) return '';
    
    // Convertir de dd/mm/yyyy a yyyy-mm-dd
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return '';
  }

  loadTipoEstadoOptions(): void {
    const request: IOpcionesRequest = {
      sTabla: 'comsec',
      sCampo: 'TipoEstado',
      iIdioma: 0
    };

    console.log('Request Opciones Tipo Estado:', request);

    this.sectoresService.getOpciones(request).subscribe({
      next: (response) => {
        console.log('Response Opciones Tipo Estado:', response);
        if (response.success && response.data) {
          this.tipoEstadoOptions = response.data.map(item => ({
            label: item.sOpcion,
            value: item.sValor
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar opciones tipo estado:', error);
      }
    });
  }

  loadVariableFinancieraOptions(): void {
    const request: IRelacionRequest = {
      que: 4,
      tope: '9999',
      cmd: '',
      numero_relacion: 0,
      tipo: '',
      descripcion: '',
      formula: '',
      periodicidad: '',
      tipoVariable: ''
    };

    console.log('Request Variables Financieras:', request);

    this.sectoresService.getRelaciones(request).subscribe({
      next: (response: IRelacionResponse) => {
        console.log('Response Variables Financieras:', response);
        if (response.success && response.data) {
          this.variableFinancieraOptions = response.data.map(item => ({
            label: `${item.numero_relacion} | ${item.tipo} | ${item.descripcion}`,
            value: item.numero_relacion
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar variables financieras:', error);
      }
    });
  }

  save(): void {
    if (this.form.valid) {
      this.loading = true;
      
      const formData = this.form.getRawValue();
      const userSession = this.sessionService.getUserSession();
      const sUsuario = userSession && userSession.trim() !== '' ? userSession : 'admin';
      
      // Formatear fechas a dd/mm/aaaa
      const formatDate = (dateString: string): string => {
        if (!dateString) return '';
        
        // Crear la fecha usando el formato YYYY-MM-DD directamente
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month - 1 porque los meses van de 0-11
        
        const dayFormatted = date.getDate().toString().padStart(2, '0');
        const monthFormatted = (date.getMonth() + 1).toString().padStart(2, '0');
        const yearFormatted = date.getFullYear();
        
        return `${dayFormatted}/${monthFormatted}/${yearFormatted}`;
      };

      if (this.isEditMode) {
        // Modo edición: actualizar
        const request = {
          sUsuario: sUsuario,
          que: 0, // Actualizar
          tope: '',
          sector: this.itemToEdit.sector, // Usar el valor original
          sDesde: this.itemToEdit.desde, // Usar el valor original
          sHasta: this.itemToEdit.hasta, // Usar el valor original
          tipoEstado: this.itemToEdit.tipo, // Usar el valor original
          variableFinanciera: this.itemToEdit.variableFinanciera, // Usar el valor original
          valorMinimo: formData.valorMinimo,
          valorMaximo: formData.valorMaximo,
          valorPromedio: formData.valorPromedio
        };

        console.log('Request Update Finanza:', request);

        this.sectoresService.updateFinanza(request).subscribe({
          next: (response) => {
            console.log('Response Update Finanza:', response);
            if (response.success && response.data) {
              this.saved.emit({ success: true, message: 'Finanza actualizada correctamente' });
            } else {
              this.saved.emit({ success: false, message: 'Error al actualizar la finanza' });
            }
            this.loading = false;
          },
          error: (error) => {
            console.error('Error al actualizar finanza:', error);
            this.saved.emit({ success: false, message: 'Error al actualizar la finanza' });
            this.loading = false;
          }
        });
      } else {
        // Modo nuevo: crear
        const request = {
          sUsuario: sUsuario,
          que: 1, // Crear
          tope: '',
          sector: formData.sector,
          sDesde: formatDate(formData.desde),
          sHasta: formatDate(formData.hasta),
          tipoEstado: formData.tipoEstado,
          variableFinanciera: formData.variableFinanciera,
          valorMinimo: formData.valorMinimo,
          valorMaximo: formData.valorMaximo,
          valorPromedio: formData.valorPromedio
        };

        console.log('Request Save Finanza:', request);

        this.sectoresService.saveFinanza(request).subscribe({
          next: (response) => {
            console.log('Response Save Finanza:', response);
            if (response.success && response.data) {
              this.saved.emit({ success: true, message: 'Finanza guardada correctamente' });
            } else {
              this.saved.emit({ success: false, message: 'Error al guardar la finanza' });
            }
            this.loading = false;
          },
          error: (error) => {
            console.error('Error al guardar finanza:', error);
            this.saved.emit({ success: false, message: 'Error al guardar la finanza' });
            this.loading = false;
          }
        });
      }
    } else {
      this.saved.emit({ success: false, message: 'Por favor complete todos los campos requeridos' });
    }
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
